<template>
  <header
    class="AppHeader"
    :class="{
      'AppHeader--training': isTraining,
      'AppHeader--resume': showResume
    }"
  >
    <b-container>
      <b-row>
        <b-col>
          <div
            class="site-header"
            :class="{ 'site-header--has-menu': inApplication }"
          >
            <button
              @click="toggleMenu"
              v-if="inApplication || isAuthenticated"
              class="site-header__menu-trigger"
              aria-label="Steps"
              :aria-expanded="(!!hasMenuOpen).toString()"
            >
              <font-awesome-icon
                v-if="!hasMenuOpen"
                class="icon fa-2x"
                icon="bars"
              />
              <font-awesome-icon v-else class="icon fa-2x" icon="times" />
            </button>
            <img
              class="site-header__logo"
              src="@/assets/Building-Blocks-Logo-1.png"
              alt="MN The Building Blocks logo"
            />

            <h1 class="site-header__title site-header__title--full">
              {{ titleFull }}
            </h1>
            <h1 class="site-header__title site-header__title--short">
              {{ titleShort }}
            </h1>

            <div class="site-header__actions">
              <ChipDropdown
                v-if="isTraining"
                :menuItems="menuItems"
                subHeader="Training Options"
                chipClass="training-dropdown"
              >
                <template v-slot:title>Training - PI</template>
              </ChipDropdown>

              <div
                v-if="inApplication"
                class="site-header__status"
                :class="[
                  { 'site-header__status--saving': isSaving },
                  { 'site-header__status--saved': isSaved }
                ]"
              >
                <template v-if="isSaving">
                  <font-awesome-icon
                    class="site-header__status-icon"
                    icon="circle-notch"
                  />
                  <span class="site-header__status-text">Saving&hellip;</span>
                </template>

                <template v-if="isSaved">
                  <font-awesome-icon
                    class="site-header__status-icon"
                    :icon="['far', 'check-circle']"
                  />
                  <span class="site-header__status-text">Saved</span>
                </template>
              </div>

              <div v-if="showResume" class="site-header__resume">
                <router-link
                  to="/login/resume"
                  class="site-header__resume-link"
                >
                  <img
                    class="site-header__resume-icon"
                    src="@/assets/icon-edit.svg"
                  />
                  Resume application
                </router-link>
              </div>
            </div>
            <IconDropdown
              v-if="inApplication || isAuthenticated"
              :menuItems="settingMenuItems"
              subHeader="Application options"
              iconLabel="Setting"
              icon="mdi-cog-outline"
              iconClass="settings-menu"
              color="blue lighten-4"
              :iconClick="onSettingIconClick"
            >
              <template v-slot:footer>
                <v-divider v-if="settingMenuItems.length"></v-divider>
                <v-list-item
                  class="settings-menu__item--logout"
                  @click="openLogoutModal"
                >
                  <v-icon size="20" color="primary" class="mr-2"
                    >mdi-logout</v-icon
                  >Sign out
                </v-list-item>
              </template>
            </IconDropdown>
          </div>
        </b-col>
      </b-row>
    </b-container>
  </header>
</template>

<script>
const ChipDropdown = () => import('@/components/app/ChipDropdown')
import IconDropdown from '@/components/app/IconDropdown'
import { mapGetters } from 'vuex'
import application from '../mixins/application'

export default {
  name: 'AppHeader',
  components: { ChipDropdown, IconDropdown },
  mixins: [application],
  data() {
    return {
      defaultTitle: 'Online Emergency Assistance Application'     
    }
  },
  computed: {
    ...mapGetters(['blockNavigation']),
    isSaving() {
      return this.$store.state.saveStatus === 'saving'
    },
    isSaved() {
      return this.$store.state.saveStatus === 'saved'
    },
    isTraining() {
      return this.$store.state.inTrainingMode
    },
    hasMenuOpen() {
      return this.$store.state.showMenu
    },
    inApplication() {
      return this.$route.name === 'ApplicationView'
    },
    // display settings menu on ExistingApplication and Privacy page
    isAuthenticated() {
      return (
        this.$route.name === 'ExistingApplication' ||
        (this.$route.name === 'PilotView' && this.$store.state.auth.idToken)
      )
    },
    showResume() {
      return (
        this.$route.name === 'WelcomeView'
      )
    },
    currentApplication() {
      return this.$store.state.application
    },    
    titleFull() {
      return this.defaultTitle
    },
    titleShort() {
      return  this.defaultTitle
    },
    menuItems() {
      return [
        {
          text: 'Training - CI (Client Interface)',
          selected: true,
          disabled: true
        },
        {
          text: 'Training - SI (Staff Interface)',
          externalLink: ''
        }
      ]
    },
    settingMenuItems() {
      if (this.inApplication) {
        const self = this
        return [
          {
            icon: 'mdi-minus-circle-outline',
            text: 'Cancel this application',
            class: 'settings-menu__item--cancel-application',
            onClick() {           

              if (self.blockNavigation) {
                self.handleBlockNavigation()
                return
              }

              

              self.$store.dispatch('openModal', {
                componentName: 'ModalCancel',
                logout: true,
                size: 'small'
              })
            }
          }
        ]
      } else {
        return []
      }
    }
  },
  methods: {    
    openMenu() {
      this.$emit('open-menu')
    },
    closeMenu() {
      this.$emit('close-menu')
    },
    toggleMenu() {
      this.hasMenuOpen ? this.closeMenu() : this.openMenu()
    },   
     onSettingIconClick() {    
    },
    openLogoutModal() {
      if (this.blockNavigation) {
        this.handleBlockNavigation()
      } else {
        this.$store.dispatch('openModal', {
          componentName: 'ModalLogout',
          size: 'small'
        })
      }
    }
  }
}
</script>

<style lang="scss">
.AppHeader {
  z-index: z('header');
  position: fixed;
  width: 100%;
  border-bottom: solid 1px $color-white;
  background-color: $color-primary;
  color: $color-white;
  font-size: 1rem; // Reset to base
  line-height: 1;

  &.AppHeader--training {
    background-color: $color-training;
    border-color: $color-training;
    border-bottom-color: white;

    .site-header--has-menu .site-header__actions {
      flex: 1;
    }

    .site-header__resume {
      background-color: $color-training-secondary;
    }
  }

  .site-header {
    display: flex;
    align-items: center;
    padding-top: 1.3em;
    padding-bottom: 1.3em;
  }

  .site-header__actions {
    display: flex;
    align-items: center;
    flex: 1;
  }

  .site-header--has-menu {
    // flex-direction: row-reverse;

    .dropdown__button {
      margin-left: 1.5em;
    }
  }

  .site-header__logo {
    height: 6.8em;
  }

  .site-header--has-menu .site-header__logo {
    margin-right: auto;
  }

  .site-header__title {
    margin-bottom: 0;
    margin-left: 1em;
    margin-right: 1em;
    font-weight: $text-medium;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &.site-header__title--full {
      display: none;
    }
  }

  .site-header__menu-trigger {
    // Set a fixed width so as to avoid the text of the title jumping when the
    // icon is swapped between open and close states.
    @include button-reset;
    width: 18px;
  }

  .site-header__status {
    display: none;
    align-items: center;
    margin-left: auto;
    font-weight: $text-bold;
  }

  .site-header__status-icon {
    font-size: 2.5rem;
  }

  .site-header__status--saving .site-header__status-icon {
    animation: statusSpin 3s infinite linear;
  }

  .site-header__status-text {
    margin-left: 1rem;
    font-size: 1.5em;
  }

  .site-header__resume {
    margin-left: auto;
    padding: 0.7rem 1rem;
    border-radius: 4px;
    background-color: $color-primary;
    font-weight: $text-bold;
    font-size: $text-small;
    outline: none;
    cursor: pointer;
    transition: $basic-transition;
    white-space: nowrap;

    &:hover,
    &:focus {
      background-color: $color-secondary;
    }
  }

  .site-header__resume-link {
    display: flex;
    align-items: center;
    color: $color-white;
    text-decoration: none;
  }

  .site-header__resume-icon {
    margin-right: 0.8rem;
    width: 2rem;
  }
}

// #DSE-1196: Custom MQ, deliberately kept out of the wider styles as it is
// unique to this component alone.
@media (min-width: 720px) {
  .AppHeader .site-header__title {
    display: block;
  }
}

@include desktop {
  .AppHeader {
    z-index: z('menu');
    height: 100px;
    .logo {
      max-width: 42px;
      margin-right: 1rem;
    }
    .title {
      font-size: 17px;
      line-height: normal;
    }

    .site-header__status {
      display: flex;
    }

    .site-header--has-menu {
      flex-direction: row-reverse;

      .dropdown__button {
        margin-left: 0em;
      }

      .site-header__actions {
        flex: 1;
      }
    }

    .site-header {
      flex-direction: row;
    }

    .site-header--has-menu .site-header__logo {
      margin-right: 100;
    }

    .site-header__title {
      font-size: 2.1em;

      &.site-header__title--full {
        display: block;
      }

      &.site-header__title--short {
        display: none;
      }
    }

    .site-header__menu-trigger {
      display: none;
    }
  }
}

@include tablet {
  .dropdown__button {
    margin-left: 1.5rem;
  }

  .site-header--has-menu {
    .site-header__logo {
      display: none;
    }
  }
}

@include tablet-wide {
  .site-header--has-menu {
    .site-header__logo {
      display: none;
    }
  }
}

@include mobile {
  .AppHeader {
    .site-header__title {
      font-size: 1.7em;
      letter-spacing: -1px;
    }

    &--training {
      &.AppHeader--resume .site-header {
        align-items: flex-start;
      }

      .site-header {
        &__logo {
          height: 32px;
        }

        &--has-menu .site-header__actions {
          flex-direction: row;
          align-items: center;
          margin-left: 0;

          .dropdown__button {
            margin-left: 1.5rem;
          }
        }

        &__actions {
          flex-direction: column;
          align-items: flex-start;
          margin-left: 1.5rem;
        }

        &__resume {
          margin-top: 1.5rem;
          margin-left: 0;
        }

        .dropdown__button {
          margin-left: 0;
          margin-top: 0;
        }
      }
    }
  }
}

@media print {
  .AppHeader {
    display: none;
  }
}

@keyframes statusSpin {
  100% {
    transform: rotate(360deg);
  }
}
</style>
